/**
 * CLI Tag Commands
 *
 * List, create, update, delete, and assign org-scoped tags.
 *
 * @module tag
 */

import process from 'node:process';
import { resolveConfig, getArgValue, hasFlag } from './auth.js';
import { apiRequest } from './cli-helpers.js';

export type TagColor =
  | 'red'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'teal'
  | 'blue'
  | 'indigo'
  | 'purple'
  | 'pink'
  | 'gray';

const VALID_COLORS: TagColor[] = [
  'red',
  'orange',
  'yellow',
  'green',
  'teal',
  'blue',
  'indigo',
  'purple',
  'pink',
  'gray',
];

const VALID_ENTITY_TYPES = ['issue', 'workflow', 'attachment'] as const;
export type TagEntityType = (typeof VALID_ENTITY_TYPES)[number];

export type Tag = {
  id: string;
  name: string;
  color: TagColor;
  description: string | null;
};

type ListResponse = { ok: boolean; error?: string; data: Tag[] };
type StatsResponse = {
  ok: boolean;
  error?: string;
  data: Array<Tag & { assignmentCount: number }>;
};
type MutateResponse = { ok: boolean; error?: string; tag?: Tag };
type OkResponse = { ok: boolean; error?: string };

function printTagHelp(): void {
  console.log(
    [
      'Usage: canary tag <sub-command> [options]',
      '',
      'Sub-commands:',
      '  list [--with-stats] [--json]              List tags in the org',
      '  create --name <name> --color <color> [--description <text>]',
      '                                             Create a new tag',
      '  update <id> [--name] [--color] [--description]',
      '                                             Update an existing tag',
      '  delete <id>                                Delete a tag',
      '  assign --entity-type <t> --entity-id <id> --tags <id,id,...>',
      '                                             Replace tags on an entity',
      '  for --entity-type <t> --entity-id <id>    List tags on an entity',
      '',
      'Entity types: ' + VALID_ENTITY_TYPES.join(', '),
      'Colors:       ' + VALID_COLORS.join(', '),
      '',
      'Tag names can also be used directly on workflow create/patch:',
      '  canary workflow create --tags smoke,regression ...',
      '  canary workflow patch <id> --tags-add flaky',
      '',
      'Common options:',
      '  --env <env>          Target environment (prod, dev, local)',
      '  --api-url <url>      API URL override',
      '  --token <key>        API token override',
      '  --json               Output raw JSON',
    ].join('\n')
  );
}

export async function fetchAllTags(apiUrl: string, token: string): Promise<Tag[]> {
  const result = await apiRequest<ListResponse>(apiUrl, token, 'GET', '/org/tags');
  if (!result.ok) {
    throw new Error(result.error ?? 'Failed to fetch tags');
  }
  return result.data ?? [];
}

function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp: number[] = Array(n + 1)
    .fill(0)
    .map((_, i) => i);
  for (let i = 1; i <= m; i++) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= n; j++) {
      const tmp = dp[j];
      dp[j] = Math.min(
        dp[j] + 1,
        dp[j - 1] + 1,
        prev + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
      prev = tmp;
    }
  }
  return dp[n];
}

/**
 * Resolve a comma-separated list of tag names to IDs. Throws with an actionable
 * error (including "did you mean" suggestions) if any name is unknown.
 */
export function resolveTagNames(
  tags: Tag[],
  requested: string[]
): { tagIds: string[]; unknown: string[] } {
  const byLowerName = new Map(tags.map((t) => [t.name.toLowerCase(), t]));
  const tagIds: string[] = [];
  const unknown: string[] = [];
  for (const raw of requested) {
    const name = raw.trim();
    if (!name) continue;
    const match = byLowerName.get(name.toLowerCase());
    if (match) {
      tagIds.push(match.id);
    } else {
      unknown.push(name);
    }
  }
  return { tagIds, unknown };
}

export function formatUnknownTagsError(
  unknown: string[],
  tags: Tag[],
  exampleWorkflowId?: string
): string {
  const lines: string[] = [];
  for (const name of unknown) {
    lines.push(`Tag "${name}" not found.`);
    const suggestions = tags
      .map((t) => ({ tag: t, dist: levenshtein(name.toLowerCase(), t.name.toLowerCase()) }))
      .filter((s) => s.dist <= 2)
      .sort((a, b) => a.dist - b.dist)
      .slice(0, 3);
    if (suggestions.length > 0) {
      lines.push('');
      lines.push('Did you mean one of these existing tags?');
      for (const s of suggestions) {
        lines.push(`  - ${s.tag.name}  (id: ${s.tag.id})`);
      }
    }
    lines.push('');
  }
  lines.push('List all tags:       canary tag list');
  lines.push(`Create this tag:     canary tag create --name "${unknown[0]}" --color blue`);
  if (exampleWorkflowId) {
    lines.push(
      `Then retry with:     canary workflow patch ${exampleWorkflowId} --tags ${unknown.join(',')}`
    );
  }
  return lines.join('\n');
}

async function handleList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const withStats = hasFlag(argv, '--with-stats');
  const jsonOutput = hasFlag(argv, '--json');

  if (withStats) {
    const result = await apiRequest<StatsResponse>(apiUrl, token, 'GET', '/org/tags/stats');
    if (!result.ok) {
      console.error(`Error: ${result.error}`);
      process.exit(1);
    }
    if (jsonOutput) {
      console.log(JSON.stringify({ data: result.data }, null, 2));
      return;
    }
    if (!result.data || result.data.length === 0) {
      console.log('No tags found.');
      return;
    }
    const idW = 36;
    const nameW = 24;
    const colorW = 8;
    const countW = 6;
    console.log(
      ['ID'.padEnd(idW), 'Name'.padEnd(nameW), 'Color'.padEnd(colorW), 'Uses'.padEnd(countW)].join(
        '  '
      )
    );
    for (const t of result.data) {
      console.log(
        [
          t.id.padEnd(idW),
          t.name.padEnd(nameW),
          t.color.padEnd(colorW),
          String(t.assignmentCount).padEnd(countW),
        ].join('  ')
      );
    }
    return;
  }

  const tags = await fetchAllTags(apiUrl, token);
  if (jsonOutput) {
    console.log(JSON.stringify({ data: tags }, null, 2));
    return;
  }
  if (tags.length === 0) {
    console.log('No tags found.');
    return;
  }
  const idW = 36;
  const nameW = 24;
  const colorW = 8;
  console.log(['ID'.padEnd(idW), 'Name'.padEnd(nameW), 'Color'.padEnd(colorW)].join('  '));
  for (const t of tags) {
    console.log([t.id.padEnd(idW), t.name.padEnd(nameW), t.color.padEnd(colorW)].join('  '));
  }
}

async function handleCreate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const name = getArgValue(argv, '--name');
  const color = getArgValue(argv, '--color') as TagColor | undefined;
  const description = getArgValue(argv, '--description');
  const jsonOutput = hasFlag(argv, '--json');

  if (!name) {
    console.error('Error: --name is required.');
    process.exit(1);
  }
  if (!color) {
    console.error(`Error: --color is required. Options: ${VALID_COLORS.join(', ')}`);
    process.exit(1);
  }
  if (!VALID_COLORS.includes(color)) {
    console.error(`Error: invalid color "${color}". Must be one of: ${VALID_COLORS.join(', ')}`);
    process.exit(1);
  }

  const body: Record<string, unknown> = { name, color };
  if (description) body.description = description;

  const result = await apiRequest<MutateResponse>(apiUrl, token, 'POST', '/org/tags', body);
  if (!result.ok || !result.tag) {
    console.error(`Error: ${result.error ?? 'create failed'}`);
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify(result.tag, null, 2));
    return;
  }
  console.log(`\u2713 Created tag "${result.tag.name}"`);
  console.log(`  ID: ${result.tag.id}`);
  console.log(`  Color: ${result.tag.color}`);
}

async function handleUpdate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv.find((a) => !a.startsWith('-'));
  if (!id) {
    console.error('Error: missing tag ID. Usage: canary tag update <id> [--name] [--color] [--description]');
    process.exit(1);
  }

  const name = getArgValue(argv, '--name');
  const color = getArgValue(argv, '--color') as TagColor | undefined;
  const description = getArgValue(argv, '--description');
  const jsonOutput = hasFlag(argv, '--json');

  if (!name && !color && description === undefined) {
    console.error('Error: provide at least one of: --name, --color, --description');
    process.exit(1);
  }
  if (color && !VALID_COLORS.includes(color)) {
    console.error(`Error: invalid color "${color}". Must be one of: ${VALID_COLORS.join(', ')}`);
    process.exit(1);
  }

  const body: Record<string, unknown> = {};
  if (name) body.name = name;
  if (color) body.color = color;
  if (description !== undefined) body.description = description;

  const result = await apiRequest<MutateResponse>(apiUrl, token, 'PUT', `/org/tags/${id}`, body);
  if (!result.ok || !result.tag) {
    console.error(`Error: ${result.error ?? 'update failed'}`);
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify(result.tag, null, 2));
    return;
  }
  console.log(`\u2713 Updated tag ${result.tag.id}`);
}

async function handleDelete(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv.find((a) => !a.startsWith('-'));
  if (!id) {
    console.error('Error: missing tag ID.');
    process.exit(1);
  }
  const jsonOutput = hasFlag(argv, '--json');

  const result = await apiRequest<OkResponse>(apiUrl, token, 'DELETE', `/org/tags/${id}`);
  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'delete failed'}`);
    process.exit(1);
  }
  if (jsonOutput) {
    console.log(JSON.stringify({ ok: true, id }, null, 2));
    return;
  }
  console.log(`\u2713 Deleted tag ${id}`);
}

function parseEntityType(raw: string | undefined): TagEntityType {
  if (!raw || !VALID_ENTITY_TYPES.includes(raw as TagEntityType)) {
    console.error(
      `Error: --entity-type required, must be one of: ${VALID_ENTITY_TYPES.join(', ')}`
    );
    process.exit(1);
  }
  return raw as TagEntityType;
}

async function handleAssign(argv: string[], apiUrl: string, token: string): Promise<void> {
  const entityType = parseEntityType(getArgValue(argv, '--entity-type'));
  const entityId = getArgValue(argv, '--entity-id');
  const tagsArg = getArgValue(argv, '--tags');
  const jsonOutput = hasFlag(argv, '--json');

  if (!entityId) {
    console.error('Error: --entity-id is required.');
    process.exit(1);
  }
  if (!tagsArg) {
    console.error('Error: --tags is required (comma-separated tag IDs, or use `canary tag for` to inspect current tags).');
    process.exit(1);
  }

  const tagIds = tagsArg.split(',').map((s) => s.trim()).filter(Boolean);

  const result = await apiRequest<OkResponse>(apiUrl, token, 'PUT', '/org/tags/entity', {
    entityType,
    entityId,
    tagIds,
  });
  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'assign failed'}`);
    process.exit(1);
  }
  if (jsonOutput) {
    console.log(JSON.stringify({ ok: true, entityType, entityId, tagIds }, null, 2));
    return;
  }
  console.log(`\u2713 Set ${tagIds.length} tag(s) on ${entityType} ${entityId}`);
}

async function handleFor(argv: string[], apiUrl: string, token: string): Promise<void> {
  const entityType = parseEntityType(getArgValue(argv, '--entity-type'));
  const entityId = getArgValue(argv, '--entity-id');
  const jsonOutput = hasFlag(argv, '--json');

  if (!entityId) {
    console.error('Error: --entity-id is required.');
    process.exit(1);
  }

  const result = await apiRequest<ListResponse>(
    apiUrl,
    token,
    'GET',
    `/org/tags/entity/${entityType}/${entityId}`
  );
  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }
  if (jsonOutput) {
    console.log(JSON.stringify({ data: result.data }, null, 2));
    return;
  }
  if (!result.data || result.data.length === 0) {
    console.log('(no tags)');
    return;
  }
  for (const t of result.data) {
    console.log(`  ${t.id}  ${t.name}  [${t.color}]`);
  }
}

/**
 * Apply --tags / --tags-add / --tags-remove / --tag-id flags to a workflow.
 *
 * --tags        Replace the workflow's tag set with the given names.
 * --tags-add    Merge these names into the existing set.
 * --tags-remove Remove these names from the existing set.
 * --tag-id      Raw tag IDs (comma-separated), bypasses name resolution.
 *
 * --tags and (--tags-add | --tags-remove | --tag-id) are mutually exclusive.
 * Throws on unknown names with a helpful "did you mean" message before any
 * mutation occurs.
 */
export async function applyWorkflowTagFlags(opts: {
  apiUrl: string;
  token: string;
  argv: string[];
  workflowId: string;
}): Promise<{ applied: boolean; tagIds: string[] } | null> {
  const { apiUrl, token, argv, workflowId } = opts;

  const replace = getArgValue(argv, '--tags');
  const add = getArgValue(argv, '--tags-add');
  const remove = getArgValue(argv, '--tags-remove');
  const rawIds = getArgValue(argv, '--tag-id');

  if (!replace && !add && !remove && !rawIds) return null;

  if (replace && (add || remove)) {
    throw new Error('--tags cannot be combined with --tags-add or --tags-remove');
  }

  const splitCsv = (s: string | undefined) =>
    s ? s.split(',').map((x) => x.trim()).filter(Boolean) : [];

  const replaceNames = splitCsv(replace);
  const addNames = splitCsv(add);
  const removeNames = splitCsv(remove);
  const extraIds = splitCsv(rawIds);

  const allNames = [...replaceNames, ...addNames, ...removeNames];
  const allTags = allNames.length > 0 ? await fetchAllTags(apiUrl, token) : [];

  const resolveOrThrow = (names: string[]): string[] => {
    if (names.length === 0) return [];
    const { tagIds, unknown } = resolveTagNames(allTags, names);
    if (unknown.length > 0) {
      throw new Error(formatUnknownTagsError(unknown, allTags, workflowId));
    }
    return tagIds;
  };

  let finalIds: string[];

  if (replace !== undefined) {
    finalIds = [...resolveOrThrow(replaceNames), ...extraIds];
  } else {
    // add/remove: merge with current tags
    const currentRes = await apiRequest<ListResponse>(
      apiUrl,
      token,
      'GET',
      `/org/tags/entity/workflow/${workflowId}`
    );
    if (!currentRes.ok) {
      throw new Error(currentRes.error ?? 'failed to read current tags');
    }
    const current = new Set((currentRes.data ?? []).map((t) => t.id));
    for (const id of resolveOrThrow(addNames)) current.add(id);
    for (const id of extraIds) current.add(id);
    const removeIds = new Set(resolveOrThrow(removeNames));
    for (const id of removeIds) current.delete(id);
    finalIds = Array.from(current);
  }

  // De-dupe while preserving order
  const uniq = Array.from(new Set(finalIds));

  const result = await apiRequest<OkResponse>(apiUrl, token, 'PUT', '/org/tags/entity', {
    entityType: 'workflow',
    entityId: workflowId,
    tagIds: uniq,
  });
  if (!result.ok) {
    throw new Error(result.error ?? 'failed to assign tags');
  }
  return { applied: true, tagIds: uniq };
}

export async function runTag(argv: string[]): Promise<void> {
  const [subCommand, ...rest] = argv;

  if (!subCommand || subCommand === 'help' || hasFlag(argv, '--help', '-h')) {
    printTagHelp();
    return;
  }

  const { apiUrl, token } = await resolveConfig(rest);

  switch (subCommand) {
    case 'list':
      await handleList(rest, apiUrl, token);
      return;
    case 'create':
      await handleCreate(rest, apiUrl, token);
      return;
    case 'update':
      await handleUpdate(rest, apiUrl, token);
      return;
    case 'delete':
      await handleDelete(rest, apiUrl, token);
      return;
    case 'assign':
      await handleAssign(rest, apiUrl, token);
      return;
    case 'for':
      await handleFor(rest, apiUrl, token);
      return;
    default:
      console.error(`Unknown tag sub-command: ${subCommand}`);
      printTagHelp();
      process.exit(1);
  }
}
